// Sales Pulse — the sales team's daily action list. Three tabs:
//   Pulse    — company KPIs (window vs prior, MTD vs same-days last month,
//              YTD vs same-days last year) + who needs a call TODAY: at-risk,
//              fading and growing accounts, straight from per-customer health.
//   Customers— the full radar: every account with status, recency, window vs
//              prior revenue, YTD vs LY. Search, filter, sort, export, drill.
//   Movers   — top gainers & decliners (items / collections / customers),
//              current window vs the prior window of the same length.
//
// Backend: SalesPulseController (api/SalesPulse/*). Statuses are computed
// server-side so counts and rows always agree. Revenue = ExtPrice.
import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Button, IconButton, TextField, MenuItem,
  ToggleButton, ToggleButtonGroup, Tooltip as MuiTooltip, CircularProgress,
  Skeleton, Alert, InputAdornment, Tabs, Tab, Table, TableHead, TableBody,
  TableRow, TableCell, Select, Drawer, Avatar,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  MdMonitorHeart, MdRefresh, MdFileDownload, MdSearch, MdClose,
  MdArrowUpward, MdArrowDownward, MdFilterListOff,
} from 'react-icons/md';
import {
  ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import * as XLSX from 'xlsx';
import { API_ENDPOINTS, apiCall } from '../config/api';

const ACCENT = '#00897B';
const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 });
const usdC = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', notation: 'compact', maximumFractionDigits: 1 });
const num0 = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const money = (n, compact = false) => (compact ? usdC : usd0).format(Number(n) || 0);
const count = (n) => num0.format(Number(n) || 0);
const clean = (s) => String(s ?? '').split(String.fromCharCode(0)).join('').trim();
const pctDelta = (cur, prev) => {
  const c = Number(cur) || 0; const p = Number(prev) || 0;
  if (p === 0) return c > 0 ? null : 0; // null = "new" (no base)
  return (c - p) / p;
};

const STATUS = {
  growing: { label: 'Growing', color: '#009E73', desc: 'current window ≥115% of the prior window' },
  steady: { label: 'Steady', color: '#0072B2', desc: 'ordering normally' },
  new: { label: 'New', color: '#7C4DFF', desc: 'first order within the last 120 days' },
  fading: { label: 'Fading', color: '#E69F00', desc: 'current window ≤70% of the prior window' },
  'at-risk': { label: 'At risk', color: '#D55E00', desc: 'ordered regularly before, nothing in 60–180 days' },
  quiet: { label: 'Quiet', color: '#8D6E63', desc: 'smaller account, nothing in 60–180 days' },
  lost: { label: 'Lost', color: '#9E9E9E', desc: 'no orders in 180+ days' },
};
const STATUS_ORDER = ['growing', 'steady', 'new', 'fading', 'at-risk', 'quiet', 'lost'];

const WINDOWS = [30, 60, 90, 180];
const SORTS = [
  { key: 'cur', label: 'Window revenue' },
  { key: 'deltaDown', label: 'Biggest drop $' },
  { key: 'deltaUp', label: 'Biggest gain $' },
  { key: 'daysSince', label: 'Longest silent' },
  { key: 'r365', label: '365-day revenue' },
  { key: 'lifetime', label: 'Total revenue (all data)' },
];
const MOVER_DIMS = [
  { key: 'item', label: 'Items' },
  { key: 'collection', label: 'Collections' },
  { key: 'customer', label: 'Customers' },
];
const PAGE = 100;

function DeltaText({ cur, prev, compact = true }) {
  if (!(Number(cur) || 0) && !(Number(prev) || 0)) {
    return <Typography variant="caption" color="text.secondary">—</Typography>;
  }
  const d = pctDelta(cur, prev);
  if (d === null) return <Typography variant="caption" sx={{ color: '#7C4DFF', fontWeight: 700 }}>from 0</Typography>;
  const up = d >= 0;
  return (
    <Stack direction="row" spacing={0.25} alignItems="center" component="span" sx={{ display: 'inline-flex' }}>
      {up ? <MdArrowUpward size={13} color="#009E73" /> : <MdArrowDownward size={13} color="#D55E00" />}
      <Typography variant="caption" sx={{ fontWeight: 700, color: up ? '#009E73' : '#D55E00' }}>
        {`${Math.abs(d * 100) >= 1000 ? '999+' : Math.round(Math.abs(d * 100))}%`}
      </Typography>
      {!compact && <Typography variant="caption" color="text.secondary">vs {money(prev, true)}</Typography>}
    </Stack>
  );
}

export default function SalesPulse() {
  const theme = useTheme();
  const dark = theme.palette.mode === 'dark';

  const [window_, setWindow] = useState(90);
  const [tab, setTab] = useState(0);
  const [pulse, setPulse] = useState(null);
  const [pulseLoading, setPulseLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);

  const [qText, setQText] = useState('');
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [sort, setSort] = useState('cur');
  const [page, setPage] = useState(0);

  const [moverDim, setMoverDim] = useState('item');
  const [movers, setMovers] = useState(null);
  const [moversLoading, setMoversLoading] = useState(false);

  const [drill, setDrill] = useState(null);          // customer {id, name}
  const [drillData, setDrillData] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [drillError, setDrillError] = useState('');
  const [brokenThumbs, setBrokenThumbs] = useState(() => new Set());

  const pulseAbort = useRef(null);
  const moversAbort = useRef(null);
  const drillAbort = useRef(null);

  useEffect(() => {
    const t = setTimeout(() => setQ(qText.trim()), 350);
    return () => clearTimeout(t);
  }, [qText]);
  useEffect(() => { setPage(0); }, [q, statusFilter, sort, window_]);

  useEffect(() => {
    if (pulseAbort.current) pulseAbort.current.abort();
    const ac = new AbortController();
    pulseAbort.current = ac;
    setPulseLoading(true); setError('');
    apiCall(`${API_ENDPOINTS.SALES_PULSE_PULSE}?window=${window_}`, { signal: ac.signal })
      .then((r) => { if (pulseAbort.current === ac) { setPulse(r); setPulseLoading(false); } })
      .catch((e) => {
        if (e.name === 'AbortError' || pulseAbort.current !== ac) return;
        setError(e.message || 'Failed to load pulse'); setPulseLoading(false);
      });
    return () => ac.abort();
  }, [window_, refreshTick]);

  useEffect(() => {
    if (tab !== 2) return undefined;
    if (moversAbort.current) moversAbort.current.abort();
    const ac = new AbortController();
    moversAbort.current = ac;
    setMoversLoading(true);
    apiCall(`${API_ENDPOINTS.SALES_PULSE_MOVERS}?window=${window_}&dim=${moverDim}`, { signal: ac.signal })
      .then((r) => { if (moversAbort.current === ac) { setMovers(r); setMoversLoading(false); } })
      .catch((e) => {
        if (e.name === 'AbortError' || moversAbort.current !== ac) return;
        setError(e.message || 'Failed to load movers'); setMoversLoading(false);
      });
    return () => ac.abort();
  }, [tab, moverDim, window_, refreshTick]);

  useEffect(() => {
    if (!drill?.id) { setDrillData(null); setDrillError(''); return undefined; }
    if (drillAbort.current) drillAbort.current.abort();
    const ac = new AbortController();
    drillAbort.current = ac;
    setDrillLoading(true); setDrillError(''); setDrillData(null);
    apiCall(`${API_ENDPOINTS.SALES_PULSE_CUSTOMER}?id=${encodeURIComponent(drill.id)}&window=${window_}`, { signal: ac.signal })
      .then((r) => { if (drillAbort.current === ac) { setDrillData(r); setDrillLoading(false); } })
      .catch((e) => {
        if (e.name === 'AbortError' || drillAbort.current !== ac) return;
        setDrillError(e.message || 'Failed to load customer'); setDrillLoading(false);
      });
    return () => ac.abort();
  }, [drill, window_]);

  const kpi = pulse?.kpi || {};
  // the warehouse only holds history from this date — "lifetime" would overclaim
  const sinceLabel = kpi.dataFloor ? `since ${clean(kpi.dataFloor).slice(0, 7)}` : 'lifetime';
  const statusCounts = pulse?.statusCounts || {};
  const customers = useMemo(() => (pulse?.customers || []).map((c) => ({
    ...c, id: clean(c.id), name: clean(c.name) || clean(c.id), status: clean(c.status),
  })), [pulse]);

  const filtered = useMemo(() => {
    let rows = customers;
    if (q) rows = rows.filter((c) => c.name.toLowerCase().includes(q.toLowerCase()) || c.id.toLowerCase().includes(q.toLowerCase()));
    if (statusFilter) rows = rows.filter((c) => c.status === statusFilter);
    const val = (c) => {
      switch (sort) {
        case 'deltaDown': return (Number(c.cur) || 0) - (Number(c.prev) || 0);
        case 'deltaUp': return -((Number(c.cur) || 0) - (Number(c.prev) || 0));
        case 'daysSince': return -(Number(c.daysSince) || 0);
        case 'r365': return -(Number(c.r365) || 0);
        case 'lifetime': return -(Number(c.lifetime) || 0);
        default: return -(Number(c.cur) || 0);
      }
    };
    return [...rows].sort((a, b) => val(a) - val(b) || a.name.localeCompare(b.name));
  }, [customers, q, statusFilter, sort]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE));
  useEffect(() => {
    if (page > 0 && page + 1 > totalPages) setPage(totalPages - 1);
  }, [page, totalPages]);
  const pageRows = filtered.slice(page * PAGE, (page + 1) * PAGE);

  const spotlights = useMemo(() => {
    const atRisk = customers.filter((c) => c.status === 'at-risk')
      .sort((a, b) => (Number(b.r365) || 0) - (Number(a.r365) || 0)).slice(0, 8);
    const fading = customers.filter((c) => c.status === 'fading')
      .sort((a, b) => ((Number(a.cur) || 0) - (Number(a.prev) || 0)) - ((Number(b.cur) || 0) - (Number(b.prev) || 0))).slice(0, 8);
    const growing = customers.filter((c) => c.status === 'growing')
      .sort((a, b) => ((Number(b.cur) || 0) - (Number(b.prev) || 0)) - ((Number(a.cur) || 0) - (Number(a.prev) || 0))).slice(0, 8);
    return { atRisk, fading, growing };
  }, [customers]);

  const exportCustomers = () => {
    const head = ['Customer', 'ID', 'Status', 'Last Order', 'Days Since', `Last ${window_}d`, `Prior ${window_}d`,
      'YTD', 'LY Same Days', '365d Revenue', `Total (${sinceLabel})`, 'First Order', 'Active Months'];
    const body = filtered.map((c) => [c.name, c.id, STATUS[c.status]?.label || c.status, clean(c.lastOrder),
      Number(c.daysSince) || 0, Number(c.cur) || 0, Number(c.prev) || 0, Number(c.ytd) || 0,
      Number(c.lySame) || 0, Number(c.r365) || 0, Number(c.lifetime) || 0, clean(c.firstOrder), Number(c.activeMonths) || 0]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...body]), 'Customer Radar');
    XLSX.writeFile(wb, `Sales_Pulse_Customers_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };
  const exportMovers = () => {
    if (!movers) return;
    const head = [moverDim === 'item' ? 'Item' : moverDim === 'collection' ? 'Collection' : 'Customer',
      'Label', `Last ${window_}d`, `Prior ${window_}d`, 'Delta', 'Units', 'Prior Units'];
    const rows = (arr) => (arr || []).map((m) => [clean(m.key), clean(m.label), Number(m.cur) || 0,
      Number(m.prev) || 0, Number(m.delta) || 0, Number(m.curUnits) || 0, Number(m.prevUnits) || 0]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...rows(movers.gainers)]), 'Gainers');
    XLSX.utils.book_append_sheet(wb, XLSX.utils.aoa_to_sheet([head, ...rows(movers.decliners)]), 'Decliners');
    XLSX.writeFile(wb, `Sales_Pulse_Movers_${moverDim}_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  const openDrill = (c) => { setBrokenThumbs(new Set()); setDrill({ id: c.id, name: c.name }); };
  const gridStroke = dark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  const drillMonthly = useMemo(() => (drillData?.monthly || []).map((m) => ({
    m: clean(m.m).slice(0, 7), rev: Number(m.rev) || 0, units: Number(m.units) || 0,
  })), [drillData]);

  const statusBar = useMemo(() => {
    const total = customers.length || 1;
    return STATUS_ORDER.map((s) => ({ s, n: statusCounts[s] || 0, w: ((statusCounts[s] || 0) / total) * 100 }))
      .filter((x) => x.n > 0);
  }, [customers, statusCounts]);

  const SpotlightCard = ({ title, hint, rows, color, render }) => (
    <Paper variant="outlined" sx={{ p: 1.5, flex: 1, minWidth: 260 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color }}>{title}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>{hint}</Typography>
      <Stack spacing={0.75}>
        {rows.map((c) => (
          <Box key={c.id} onClick={() => openDrill(c)}
            sx={{ cursor: 'pointer', p: 0.75, borderRadius: 1, '&:hover': { bgcolor: dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)' } }}>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{c.name}</Typography>
            <Typography variant="caption" color="text.secondary">{render(c)}</Typography>
          </Box>
        ))}
        {!rows.length && <Typography variant="caption" color="text.secondary">Nothing here right now.</Typography>}
      </Stack>
    </Paper>
  );

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, maxWidth: 1500, mx: 'auto' }}>
      {/* header */}
      <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" spacing={1} sx={{ mb: 2 }}>
        <Box>
          <Stack direction="row" alignItems="center" spacing={1}>
            <MdMonitorHeart size={26} color={ACCENT} />
            <Typography variant="h5" sx={{ fontWeight: 800 }}>Sales Pulse</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            Who to call today: account health, momentum and movers over the last {window_} days vs the {window_} before.
            {kpi.dataFloor ? ` Sales history begins ${clean(kpi.dataFloor)}.` : ''}
            {pulse?.asOf ? ` Data as of ${pulse.asOf}.` : ''}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center">
          <ToggleButtonGroup size="small" exclusive value={window_} onChange={(e, v) => v && setWindow(v)}>
            {WINDOWS.map((w) => <ToggleButton key={w} value={w}>{w}D</ToggleButton>)}
          </ToggleButtonGroup>
          <MuiTooltip title="Refresh"><span>
            <IconButton onClick={() => setRefreshTick((t) => t + 1)} disabled={pulseLoading}><MdRefresh /></IconButton>
          </span></MuiTooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* KPI tiles */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 1.5, mb: 2 }}>
        {[
          { label: `Revenue · last ${window_}d`, value: money(kpi.curRev, true), sub: <DeltaText cur={kpi.curRev} prev={kpi.prevRev} compact={false} /> },
          { label: `Units · last ${window_}d`, value: count(kpi.curUnits), sub: <DeltaText cur={kpi.curUnits} prev={kpi.prevUnits} compact={false} /> },
          { label: `Active customers · ${window_}d`, value: count(kpi.curCusts), sub: <DeltaText cur={kpi.curCusts} prev={kpi.prevCusts} compact={false} /> },
          { label: 'Month to date', value: money(kpi.mtdRev, true), sub: <DeltaText cur={kpi.mtdRev} prev={kpi.lmRev} compact={false} />, hint: 'vs the same days of last month' },
          { label: 'Year to date', value: money(kpi.ytdRev, true), sub: <DeltaText cur={kpi.ytdRev} prev={kpi.lyRev} compact={false} />, hint: 'vs the same days of last year' },
        ].map((t) => (
          <Paper key={t.label} variant="outlined" sx={{ p: 1.5 }}>
            {pulseLoading ? <Skeleton width="70%" height={34} /> : (
              <Typography variant="h6" sx={{ fontWeight: 800 }}>{t.value}</Typography>
            )}
            <Typography variant="caption" sx={{ fontWeight: 600, display: 'block' }}>{t.label}</Typography>
            {!pulseLoading && <Box>{t.sub}</Box>}
            {t.hint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{t.hint}</Typography>}
          </Paper>
        ))}
      </Box>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Pulse" />
        <Tab label={`Customer radar (${count(customers.length)})`} />
        <Tab label="Movers" />
      </Tabs>

      {/* ------------------------------- PULSE TAB ------------------------------ */}
      {tab === 0 && (
        <>
          <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Account health across {count(customers.length)} customers</Typography>
            {pulseLoading ? <Skeleton height={26} /> : (
              <>
                <Stack direction="row" sx={{ height: 14, borderRadius: 7, overflow: 'hidden', mb: 1 }}>
                  {statusBar.map(({ s, w }) => (
                    <MuiTooltip key={s} title={`${STATUS[s].label}: ${statusCounts[s]}`}>
                      <Box sx={{ width: `${w}%`, bgcolor: STATUS[s].color, minWidth: 3 }} />
                    </MuiTooltip>
                  ))}
                </Stack>
                <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
                  {statusBar.map(({ s, n }) => (
                    <Chip key={s} size="small" label={`${STATUS[s].label} ${count(n)}`}
                      onClick={() => { setStatusFilter(s); setTab(1); }}
                      sx={{ color: STATUS[s].color, borderColor: STATUS[s].color, fontWeight: 700 }} variant="outlined" />
                  ))}
                </Stack>
              </>
            )}
          </Paper>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 2 }}>
            <SpotlightCard title="At risk — call first" color="#D55E00"
              hint="regular buyers gone quiet 60–180 days · ranked by their last-365-day revenue"
              rows={pulseLoading ? [] : spotlights.atRisk}
              render={(c) => `silent ${c.daysSince} days · ${money(c.r365, true)} last 365d · last order ${clean(c.lastOrder)}`} />
            <SpotlightCard title="Fading — losing momentum" color="#E69F00"
              hint={`biggest window-over-window drops (last ${window_}d vs prior ${window_}d)`}
              rows={pulseLoading ? [] : spotlights.fading}
              render={(c) => `${money(c.cur, true)} vs ${money(c.prev, true)} · down ${money((Number(c.prev) || 0) - (Number(c.cur) || 0), true)}`} />
            <SpotlightCard title="Growing — protect & expand" color="#009E73"
              hint="biggest window-over-window gains — keep them stocked"
              rows={pulseLoading ? [] : spotlights.growing}
              render={(c) => `${money(c.cur, true)} vs ${money(c.prev, true)} · up ${money((Number(c.cur) || 0) - (Number(c.prev) || 0), true)}`} />
          </Stack>
        </>
      )}

      {/* ----------------------------- CUSTOMERS TAB ---------------------------- */}
      {tab === 1 && (
        <>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ md: 'center' }} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
            <TextField size="small" placeholder="Search customer or ID…" value={qText}
              onChange={(e) => setQText(e.target.value)} sx={{ minWidth: 240 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><MdSearch /></InputAdornment>,
                endAdornment: qText ? <InputAdornment position="end"><IconButton size="small" onClick={() => setQText('')}><MdClose size={16} /></IconButton></InputAdornment> : null,
              }} />
            <Stack direction="row" spacing={0.5} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Chip label={`All ${count(customers.length)}`} size="small" variant={statusFilter === '' ? 'filled' : 'outlined'}
                onClick={() => setStatusFilter('')} color={statusFilter === '' ? 'primary' : 'default'} />
              {STATUS_ORDER.filter((s) => statusCounts[s]).map((s) => (
                <MuiTooltip key={s} title={STATUS[s].desc}>
                  <Chip label={`${STATUS[s].label} ${count(statusCounts[s])}`} size="small"
                    variant={statusFilter === s ? 'filled' : 'outlined'}
                    onClick={() => setStatusFilter(statusFilter === s ? '' : s)}
                    sx={{ borderColor: STATUS[s].color, ...(statusFilter === s ? { bgcolor: STATUS[s].color, color: '#fff' } : { color: STATUS[s].color }) }} />
                </MuiTooltip>
              ))}
            </Stack>
            <Box sx={{ flex: 1 }} />
            <Select size="small" value={sort} onChange={(e) => setSort(e.target.value)}>
              {SORTS.map((s) => <MenuItem key={s.key} value={s.key}>{s.label}</MenuItem>)}
            </Select>
            {(q || statusFilter) && (
              <Button size="small" startIcon={<MdFilterListOff />} onClick={() => { setQText(''); setStatusFilter(''); }}>Clear</Button>
            )}
            <Button variant="outlined" size="small" startIcon={<MdFileDownload />} onClick={exportCustomers} disabled={!filtered.length}>
              Export
            </Button>
          </Stack>

          <Paper variant="outlined" sx={{ mb: 1, overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 980 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Customer</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Last order</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Last {window_}d</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Prior {window_}d</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Change</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">YTD</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">LY same days</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">365d</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pulseLoading && Array.from({ length: 10 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={9}><Skeleton height={24} /></TableCell></TableRow>
                ))}
                {!pulseLoading && pageRows.map((c) => {
                  const st = STATUS[c.status] || { label: c.status, color: '#888' };
                  return (
                    <TableRow key={c.id} hover onClick={() => openDrill(c)} sx={{ cursor: 'pointer' }}>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{c.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.id}</Typography>
                      </TableCell>
                      <TableCell>
                        <MuiTooltip title={st.desc || ''}>
                          <Chip label={st.label} size="small" sx={{ bgcolor: `${st.color}22`, color: st.color, fontWeight: 700 }} />
                        </MuiTooltip>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">{clean(c.lastOrder) || '—'}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Number(c.daysSince) >= 9999 ? '' : `${count(c.daysSince)}d ago`}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{money(c.cur, true)}</TableCell>
                      <TableCell align="right">{money(c.prev, true)}</TableCell>
                      <TableCell align="right"><DeltaText cur={c.cur} prev={c.prev} /></TableCell>
                      <TableCell align="right">{money(c.ytd, true)}</TableCell>
                      <TableCell align="right">{money(c.lySame, true)}</TableCell>
                      <TableCell align="right">{money(c.r365, true)}</TableCell>
                    </TableRow>
                  );
                })}
                {!pulseLoading && !pageRows.length && (
                  <TableRow><TableCell colSpan={9}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                      No customers match these filters.
                    </Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Paper>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary">
              {count(filtered.length)} customers · page {page + 1} / {totalPages}
            </Typography>
            <Stack direction="row" spacing={1}>
              <Button size="small" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</Button>
              <Button size="small" disabled={page + 1 >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </Stack>
          </Stack>
        </>
      )}

      {/* ------------------------------ MOVERS TAB ------------------------------ */}
      {tab === 2 && (
        <>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
            <ToggleButtonGroup size="small" exclusive value={moverDim} onChange={(e, v) => v && setMoverDim(v)}>
              {MOVER_DIMS.map((d) => <ToggleButton key={d.key} value={d.key}>{d.label}</ToggleButton>)}
            </ToggleButtonGroup>
            <Typography variant="caption" color="text.secondary">
              last {window_} days vs the {window_} before · low-volume rows filtered out
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button variant="outlined" size="small" startIcon={<MdFileDownload />} onClick={exportMovers} disabled={!movers || moversLoading}>
              Export
            </Button>
          </Stack>
          <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.5}>
            {[{ key: 'gainers', title: 'Top gainers', color: '#009E73' }, { key: 'decliners', title: 'Top decliners', color: '#D55E00' }].map((side) => {
              const rows = (movers?.[side.key] || []);
              const maxAbs = Math.max(1, ...rows.map((m) => Math.abs(Number(m.delta) || 0)));
              return (
                <Paper key={side.key} variant="outlined" sx={{ p: 1.5, flex: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: side.color, mb: 1 }}>{side.title}</Typography>
                  {moversLoading && <Stack alignItems="center" sx={{ py: 4 }}><CircularProgress size={28} /></Stack>}
                  {!moversLoading && rows.map((m) => {
                    const key = clean(m.key);
                    const label = clean(m.label);
                    const isCustomerDim = moverDim === 'customer';
                    return (
                      <Box key={key}
                        onClick={isCustomerDim ? () => { setBrokenThumbs(new Set()); setDrill({ id: key, name: label || key }); } : undefined}
                        sx={{ py: 0.6, borderBottom: `1px solid ${gridStroke}`, ...(isCustomerDim ? { cursor: 'pointer' } : {}) }}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          {moverDim === 'item' && (
                            <Avatar variant="rounded" src={!brokenThumbs.has(key) && clean(m.thumb) ? clean(m.thumb) : undefined}
                              imgProps={{ onError: () => setBrokenThumbs((prev) => new Set(prev).add(key)) }}
                              sx={{ width: 30, height: 30, fontSize: 10 }}>{key.slice(0, 2)}</Avatar>
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }} noWrap>{isCustomerDim ? (label || key) : key}</Typography>
                            <Typography variant="caption" color="text.secondary" noWrap>
                              {isCustomerDim ? key : label}{label || isCustomerDim ? ' · ' : ''}{money(m.cur, true)} vs {money(m.prev, true)}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ fontWeight: 800, color: side.color, minWidth: 76, textAlign: 'right' }}>
                            {Number(m.delta) >= 0 ? '+' : '−'}{money(Math.abs(Number(m.delta) || 0), true)}
                          </Typography>
                        </Stack>
                        <Box sx={{ height: 4, mt: 0.4, borderRadius: 2, bgcolor: `${side.color}22`, overflow: 'hidden' }}>
                          <Box sx={{ height: '100%', width: `${(Math.abs(Number(m.delta) || 0) / maxAbs) * 100}%`, bgcolor: side.color }} />
                        </Box>
                      </Box>
                    );
                  })}
                  {!moversLoading && !rows.length && (
                    <Typography variant="caption" color="text.secondary">No {side.title.toLowerCase()} above the volume floor.</Typography>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </>
      )}

      {/* customer drill drawer */}
      <Drawer anchor="right" open={!!drill} onClose={() => setDrill(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 560 }, p: 2 } }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>{drill?.name}</Typography>
            <Typography variant="caption" color="text.secondary">{drill?.id}</Typography>
          </Box>
          <IconButton onClick={() => setDrill(null)}><MdClose /></IconButton>
        </Stack>
        {drillLoading && <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>}
        {drillError && (
          <Alert severity="error" action={<Button size="small" onClick={() => { const d = drill; setDrill(null); setTimeout(() => setDrill(d), 0); }}>Retry</Button>}>
            {drillError}
          </Alert>
        )}
        {!drillLoading && !drillError && drillData && (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1, mb: 2 }}>
              {[
                { label: `Last ${window_}d`, value: money(drillData.summary?.cur, true) },
                { label: `Prior ${window_}d`, value: money(drillData.summary?.prev, true) },
                { label: 'YTD', value: money(drillData.summary?.ytd, true) },
                { label: `Total ${sinceLabel}`, value: money(drillData.summary?.lifetime, true) },
                { label: 'SKUs bought', value: count(drillData.summary?.skus) },
                { label: 'First / last', value: `${clean(drillData.summary?.firstOrder)} → ${clean(drillData.summary?.lastOrder)}`, small: true },
              ].map((t) => (
                <Paper key={t.label} variant="outlined" sx={{ p: 1 }}>
                  <Typography variant={t.small ? 'caption' : 'subtitle2'} sx={{ fontWeight: 800, display: 'block' }}>{t.value}</Typography>
                  <Typography variant="caption" color="text.secondary">{t.label}</Typography>
                </Paper>
              ))}
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Monthly revenue · last 24 months</Typography>
            <Box sx={{ width: '100%', height: 170, mb: 2 }}>
              <ResponsiveContainer>
                <ComposedChart data={drillMonthly} margin={{ top: 4, right: 8, bottom: 0, left: 0 }}>
                  <CartesianGrid stroke={gridStroke} vertical={false} />
                  <XAxis dataKey="m" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                  <YAxis tickFormatter={(v) => money(v, true)} tick={{ fontSize: 10 }} width={58} />
                  <Tooltip formatter={(v, n) => (n === 'rev' ? [money(v), 'Revenue'] : [count(v), 'Units'])}
                    contentStyle={{ background: dark ? '#1e1e2f' : '#fff', border: `1px solid ${gridStroke}`, borderRadius: 8, fontSize: 12 }} />
                  <Bar dataKey="rev" fill={ACCENT} radius={[3, 3, 0, 0]} isAnimationActive={false} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Collections · last {window_}d vs prior</Typography>
            <Table size="small" sx={{ mb: 2 }}>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Collection</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Current</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Prior</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">Change</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(drillData.topCollections || []).map((tc) => (
                  <TableRow key={clean(tc.coll)}>
                    <TableCell><Typography variant="body2" sx={{ fontWeight: 600 }}>{clean(tc.coll)}</Typography></TableCell>
                    <TableCell align="right">{money(tc.cur, true)}</TableCell>
                    <TableCell align="right">{money(tc.prev, true)}</TableCell>
                    <TableCell align="right"><DeltaText cur={tc.cur} prev={tc.prev} /></TableCell>
                  </TableRow>
                ))}
                {!(drillData.topCollections || []).length && (
                  <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.secondary">No purchases in this window.</Typography></TableCell></TableRow>
                )}
              </TableBody>
            </Table>

            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>Top items · last {window_}d</Typography>
            <Stack spacing={0.5}>
              {(drillData.topItems || []).map((it) => (
                <Stack key={clean(it.item)} direction="row" spacing={1} alignItems="center">
                  <Avatar variant="rounded" src={!brokenThumbs.has(clean(it.item)) && clean(it.thumb) ? clean(it.thumb) : undefined}
                    imgProps={{ onError: () => setBrokenThumbs((prev) => new Set(prev).add(clean(it.item))) }}
                    sx={{ width: 34, height: 34, fontSize: 11 }}>{clean(it.item).slice(0, 2)}</Avatar>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>{clean(it.item)}</Typography>
                    <Typography variant="caption" color="text.secondary" noWrap>{clean(it.coll)}</Typography>
                  </Box>
                  <Typography variant="body2">{money(it.cur, true)}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ width: 66, textAlign: 'right' }}>{count(it.units)} units</Typography>
                </Stack>
              ))}
              {!(drillData.topItems || []).length && <Typography variant="caption" color="text.secondary">No purchases in this window.</Typography>}
            </Stack>
          </>
        )}
      </Drawer>
    </Box>
  );
}
